import React, { useState, ChangeEvent } from 'react';
import styled from 'styled-components';
import { motion as m } from 'framer-motion';
import { FiX, FiCamera, FiEdit2, FiUser, FiLogOut, FiMail, FiPhone } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';

const ModalOverlay = styled(m.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled(m.div)`
  background: white;
  border-radius: 20px;
  padding: 30px;
  width: 100%;
  max-width: 400px;
  position: relative;
  overflow: hidden;
`;

const CloseButton = styled(m.button)`
  position: absolute;
  top: 20px;
  right: 20px;
  background: none;
  border: none;
  font-size: 24px;
  color: #666;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  border-radius: 50%;
  
  &:hover {
    background: #f5f5f5;
  }
`;

const ProfileHeader = styled.div`
  text-align: center;
  margin-bottom: 30px;
`;

const ProfileImageContainer = styled.div`
  position: relative;
  width: 120px;
  height: 120px;
  margin: 0 auto 20px;
`;

const ProfileImage = styled.div<{ imageUrl?: string }>`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: ${props => props.imageUrl ? `url(${props.imageUrl})` : '#f0f0f0'};
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
  font-size: 40px;
`;

const ImageUploadButton = styled(m.button)`
  position: absolute;
  bottom: 0;
  right: 0;
  width: 36px;
  height: 36px;
  background: #ff0000;
  border: none;
  border-radius: 50%;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h2`
  font-family: 'Poppins', sans-serif;
  font-size: 24px;
  color: #333;
  margin-bottom: 30px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const InputGroup = styled.div`
  position: relative;
`;

const Label = styled.label`
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  color: #666;
  margin-bottom: 8px;
  display: block;
`;

const Input = styled(m.input)`
  width: 100%;
  padding: 12px;
  border: 2px solid #eee;
  border-radius: 12px;
  font-size: 16px;
  font-family: 'Poppins', sans-serif;
  outline: none;
  transition: all 0.2s ease;
  
  &:focus {
    border-color: #ff0000;
    box-shadow: 0 0 0 2px rgba(255, 0, 0, 0.1);
  }
`;

const SaveButton = styled(m.button)`
  width: 100%;
  padding: 15px;
  background: #ff0000;
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  font-family: 'Poppins', sans-serif;
  cursor: pointer;
  margin-top: 20px;
`;

const LogoutButton = styled(m.button)`
  width: 100%;
  padding: 15px;
  background: #f8f8f8;
  color: #ff0000;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  font-family: 'Poppins', sans-serif;
  cursor: pointer;
  margin-top: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  
  &:hover {
    background: #ff0000;
    color: white;
  }
`;

const ProfileInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const InfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: #f8f8f8;
  border-radius: 8px;
  
  svg {
    color: #ff0000;
    font-size: 20px;
  }
`;

const InfoText = styled.div`
  h3 {
    font-size: 12px;
    color: #666;
    margin-bottom: 2px;
  }
  
  p {
    font-size: 14px;
    color: #333;
    font-weight: 500;
  }
`;

interface ProfileModalProps {
  onClose: () => void;
}

const ProfileModal: React.FC<ProfileModalProps> = ({ onClose }) => {
  const navigate = useNavigate();
  const [profileData, setProfileData] = useState({
    name: 'User name',
    email: 'user@example.com',
    phone: '+1234567890'
  });
  const [profileImage, setProfileImage] = useState<string | null>(null);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setProfileData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageUpload = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfileImage(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle profile update logic here
    onClose();
  };

  const handleLogout = () => {
    navigate('/login');
  };

  return (
    <ModalOverlay
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <ModalContent
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 20, opacity: 0 }}
        transition={{ duration: 0.3 }}
      >
        <CloseButton
          onClick={onClose}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          <FiX />
        </CloseButton>

        <ProfileHeader>
          <ProfileImageContainer>
            <ProfileImage imageUrl={profileImage ?? undefined}>
              {!profileImage && <FiUser />}
            </ProfileImage>
            <ImageUploadButton
              as="label"
              htmlFor="profile-image"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <FiCamera />
              <input
                type="file"
                id="profile-image"
                accept="image/*"
                onChange={handleImageUpload}
                style={{ display: 'none' }}
              />
            </ImageUploadButton>
          </ProfileImageContainer>
          <Title>Edit Profile</Title>
        </ProfileHeader>

        <Form onSubmit={handleSubmit}>
          <InputGroup>
            <Label>Full Name</Label>
            <Input
              type="text"
              name="name"
              value={profileData.name}
              onChange={handleInputChange}
              whileFocus={{ scale: 1.01 }}
            />
          </InputGroup>

          <InputGroup>
            <Label>Email</Label>
            <Input
              type="email"
              name="email"
              value={profileData.email}
              onChange={handleInputChange}
              whileFocus={{ scale: 1.01 }}
            />
          </InputGroup>

          <InputGroup>
            <Label>Phone Number</Label>
            <Input
              type="tel"
              name="phone"
              value={profileData.phone}
              onChange={handleInputChange}
              whileFocus={{ scale: 1.01 }}
            />
          </InputGroup>

          <SaveButton
            type="submit"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            Save Changes
          </SaveButton>

          {/* <ProfileInfo>
            <InfoItem>
              <FiUser />
              <InfoText>
                <h3>Username</h3>
                <p>{profileData.name.split(' ')[0]}</p>
              </InfoText>
            </InfoItem>
            <InfoItem>
              <FiMail />
              <InfoText>
                <h3>Email</h3>
                <p>{profileData.email}</p>
              </InfoText>
            </InfoItem>
            <InfoItem>
              <FiPhone />
              <InfoText>
                <h3>Phone</h3>
                <p>{profileData.phone}</p>
              </InfoText>
            </InfoItem>
          </ProfileInfo> */}

          {/* <LogoutButton
            type="button"
            onClick={handleLogout}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <FiLogOut /> Logout
          </LogoutButton> */}
        </Form>
      </ModalContent>
    </ModalOverlay>
  );
};

export default ProfileModal; 